import React, { useContext, useEffect, useMemo, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  TextField,
  InputAdornment,
  CircularProgress,
  Avatar,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import ChatiaDialogActions from "../../components/ChatiaDialogActions";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Wallet,
  Building2,
  DollarSign,
  AlertTriangle,
  Sparkles,
  Send,
  CheckCircle2,
  Clock,
  XCircle,
  Filter,
  X as XIcon,
} from "lucide-react";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useCurrencyContext } from "../../context/Currency/CurrencyContext";
import { useSearch } from "../../context/Search/SearchContext";
import useFilterDropdownStyles from "../../components/SharedStyles/useFilterDropdownStyles";
import MainContainer from "../../components/MainContainer";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 0,
      overflowY: "hidden",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 0",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },

    // Stats grid
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 12,
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr",
      },
    },
    statCard: {
      borderRadius: 12,
      padding: 16,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#ffffff"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      alignItems: "center",
      gap: 12,
      minHeight: 76,
    },
    statIcon: {
      width: 44,
      height: 44,
      borderRadius: 12,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    statContent: { flex: 1, minWidth: 0 },
    statLabel: {
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    statValue: {
      fontSize: 20,
      fontWeight: 700,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#0f172a"),
      lineHeight: 1.1,
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    statHint: {
      fontSize: 11,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginTop: 2,
    },

    // Filters bar
    filtersBar: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      alignItems: "stretch",
    },
    filterField: {
      width: 240,
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
        minHeight: 44,
        padding: "4px 12px !important",
      },
    },
    filterClearBtn: {
      padding: 4,
      marginRight: 2,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
        color: "#e53935",
      },
    },

    // Card containing table
    listCard: {
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
    },
    th: {
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontWeight: 700,
      fontSize: 11.5,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      padding: "12px 16px",
      borderBottom: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      textAlign: "left",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 3,
    },
    td: {
      padding: "14px 16px",
      borderBottom: `1px solid ${
        dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")
      }`,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      fontSize: 13.5,
      verticalAlign: "middle",
    },
    tr: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor:
          dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child td": { borderBottom: "none" },
    },

    companyCell: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
    },
    companyAvatar: {
      width: 36,
      height: 36,
      borderRadius: 10,
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      fontWeight: 700,
      fontSize: 14,
    },
    companyName: {
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 220,
    },
    companyEmail: {
      fontSize: 11.5,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 220,
    },
    planChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    dueDate: {
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    dueHint: {
      fontSize: 11.5,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginTop: 1,
    },
    valueCell: {
      fontWeight: 700,
      whiteSpace: "nowrap",
    },

    statusChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    statusActive: {
      background: "rgba(16,185,129,0.12)",
      color: "#10b981",
    },
    statusOverdue: {
      background: "rgba(229,57,53,0.12)",
      color: "#e53935",
    },
    statusTrial: {
      background: "rgba(99,102,241,0.12)",
      color: theme.palette.primary.main,
    },
    statusPending: {
      background: "rgba(245,158,11,0.12)",
      color: "#f59e0b",
    },
    statusInactive: {
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },

    payBtn: {
      borderRadius: 10,
      padding: "8px 14px",
      fontSize: 12.5,
      fontWeight: 600,
      gap: 6,
      cursor: "pointer",
      border: "none",
      display: "inline-flex",
      alignItems: "center",
      transition: "all 0.18s ease",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": { background: theme.palette.primary.dark },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    payBtnGhost: {
      borderRadius: 10,
      padding: "8px 14px",
      fontSize: 12.5,
      fontWeight: 600,
      gap: 6,
      cursor: "default",
      border: "none",
      display: "inline-flex",
      alignItems: "center",
      background: "rgba(16,185,129,0.12)",
      color: "#10b981",
    },

    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontSize: 13.5,
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: 40,
    },

    // Mobile cards
    mobileGrid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 10,
      padding: 12,
    },
    mobileCard: {
      borderRadius: 12,
      padding: 14,
      background: dsColors.surfaceMuted || (isDark ? "#0d1016" : "#fff"),
      border: `1px solid ${
        dsColors.borderLight || (isDark ? "#1a1f29" : "#e8e8e8")
      }`,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    mobileCardRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8,
    },
    mobileCardLabel: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },

    // Charge modal (padrão global ConfirmationModal-like)
    dialogPaper: {
      borderRadius: "21px 21px 12px 12px !important",
      maxHeight: "85vh !important",
      display: "flex !important",
      flexDirection: "column !important",
      overflow: "hidden !important",
      background: `${isDark ? "#121212" : "#ffffff"} !important`,
      "& .MuiDialogContent-root": {
        flex: 1,
        overflowY: "auto !important",
        overflowX: "hidden",
        background: isDark ? "#121212" : "#ffffff",
        ...(theme.scrollbarStyles || {}),
      },
      "& .MuiDialogActions-root": {
        flexShrink: 0,
        background: isDark ? "#121212" : "#ffffff",
        borderTop: `1px solid ${
          isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"
        }`,
      },
    },
    chargeHeader: {
      ...(theme.chatiaDS?.header || {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText || "#fff",
        borderBottom: "none",
      }),
      flexShrink: 0,
    },
    chargeTitle: {
      ...(theme.chatiaDS?.headerTitle || {
        fontWeight: 600,
        fontSize: 15,
        color: "inherit",
      }),
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
    },
    chargeCloseBtn: {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      color: "#ffffff",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.18)",
      },
    },
    chargeBody: {
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },
    chargeInfoBlock: {
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
      borderRadius: 10,
      padding: 12,
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    chargeInfoRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      fontSize: 13,
    },
    chargeInfoLabel: {
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    chargeInfoValue: {
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      fontWeight: 600,
      textAlign: "right",
      maxWidth: "60%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    chargeMessageLabel: {
      fontSize: 12.5,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      marginBottom: 4,
    },
    chargeMessageField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
        fontSize: 13,
      },
    },
  };
});

// === Helpers ===
const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
};

const STATUS = {
  active: "active",
  overdue: "overdue",
  trial: "trial",
  pending: "pending",
  inactive: "inactive",
};

const FINANCEIRO_VIDEO_MODE = true;

const VIDEO_PLAN_AMOUNTS = [
  39,
  49,
  59,
  79,
  97,
  127,
  147,
  197,
];

const VIDEO_PLAN_NAMES = [
  "Start",
  "Essencial",
  "Growth",
  "Business",
  "Scale",
  "Enterprise",
];

const maskEmail = (email) => {
  if (!email || typeof email !== "string" || !email.includes("@")) return email;
  const [userPart, domainPart] = email.split("@");
  const [domainName, ...domainSuffix] = domainPart.split(".");
  const maskPart = (value, visible = 2) => {
    if (!value) return "***";
    return `${value.slice(0, visible)}***`;
  };
  return `${maskPart(userPart)}@${maskPart(domainName)}${
    domainSuffix.length ? `.${domainSuffix.join(".")}` : ""
  }`;
};

const maskPhone = (phone) => {
  const raw = String(phone || "");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return phone;
  if (digits.length <= 4) return "***";
  return `${digits.slice(0, 2)} *****-${digits.slice(-4)}`;
};

const getVideoStatus = (index) => {
  if (index % 17 === 0 && index !== 0) return STATUS.overdue;
  if (index % 11 === 0) return STATUS.pending;
  if (index % 9 === 0) return STATUS.trial;
  return STATUS.active;
};

const Financeiro = () => {
  const classes = useStyles();
  const dropdownClasses = useFilterDropdownStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { selectedCurrency, getAvailableCurrencies } = useCurrencyContext();
  const { ticketSearchParam, clear } = useSearch();

  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  // Limpa busca global ao sair da rota
  useEffect(() => () => clear(), [clear]);

  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeRow, setChargeRow] = useState(null);
  const [chargeMessage, setChargeMessage] = useState("");

  const isSuperAdmin = user?.super === true;

  const currencies = getAvailableCurrencies();
  const currencyByCode = useMemo(() => {
    return currencies.reduce((acc, c) => {
      acc[c.code] = c;
      return acc;
    }, {});
  }, [currencies]);

  const formatCurrency = (value, code) => {
    const curr =
      currencyByCode[code] ||
      currencyByCode[selectedCurrency?.code] ||
      currencyByCode["USD"] ||
      { code: "USD", locale: "en-US", symbol: "$" };
    try {
      return new Intl.NumberFormat(curr.locale, {
        style: "currency",
        currency: curr.code,
      }).format(parseFloat(value) || 0);
    } catch {
      return `${curr.symbol || "$"} ${(parseFloat(value) || 0).toFixed(2)}`;
    }
  };

  // Load data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [companiesRes, invoicesRes] = await Promise.all([
          isSuperAdmin
            ? api.get("/companies/list")
            : api.get(`/companies/${user.companyId}`).then((r) => ({
                data: [r.data],
              })),
          api
            .get("/invoices/all", { params: { pageNumber: 1 } })
            .catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const cs = Array.isArray(companiesRes.data)
          ? companiesRes.data
          : [companiesRes.data].filter(Boolean);
        setCompanies(cs);
        setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
      } catch (err) {
        if (!cancelled) toastError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.companyId, isSuperAdmin]);

  // === Compute per-company status ===
  const rows = useMemo(() => {
    const today = moment().startOf("day");
    return companies
      .filter((c) => c && c.id)
      .map((c, index) => {
        const due = c.dueDate ? moment(c.dueDate) : null;
        const daysUntilDue = due ? due.diff(today, "days") : null;

        const myInvoices = invoices.filter((inv) => inv.companyId === c.id);
        const paidInvoices = myInvoices.filter((inv) => inv.status === "paid");
        const openInvoice = myInvoices.find((inv) => inv.status !== "paid");

        let status;
        if (c.status === false) {
          status = STATUS.inactive;
        } else if (due && due.isBefore(today, "day")) {
          status = STATUS.overdue;
        } else if (paidInvoices.length === 0) {
          status = STATUS.trial;
        } else if (openInvoice) {
          status = STATUS.pending;
        } else {
          status = STATUS.active;
        }

        const planAmount = parseFloat(c.plan?.amount ?? 0) || 0;
        const currency = FINANCEIRO_VIDEO_MODE
          ? "USD"
          : c.plan?.currency || selectedCurrency?.code || "USD";

        if (FINANCEIRO_VIDEO_MODE) {
          const videoStatus = getVideoStatus(index);
          const videoAmount = VIDEO_PLAN_AMOUNTS[index % VIDEO_PLAN_AMOUNTS.length];
          const videoDue =
            videoStatus === STATUS.overdue
              ? moment(today).subtract((index % 4) + 2, "days")
              : moment(today).add((index % 18) + 3, "days");

          return {
            ...c,
            due: videoDue,
            daysUntilDue: videoDue.diff(today, "days"),
            paidInvoices,
            openInvoice,
            status: videoStatus,
            planAmount: videoAmount,
            currency,
            plan: {
              ...(c.plan || {}),
              name: c.plan?.name || VIDEO_PLAN_NAMES[index % VIDEO_PLAN_NAMES.length],
              amount: videoAmount,
            },
            displayEmail: maskEmail(c.email),
            displayPhone: maskPhone(c.phone),
          };
        }

        return {
          ...c,
          due,
          daysUntilDue,
          paidInvoices,
          openInvoice,
          status,
          planAmount,
          currency,
          displayEmail: maskEmail(c.email),
          displayPhone: maskPhone(c.phone),
        };
      });
  }, [companies, invoices, selectedCurrency]);

  // Filtered rows — busca vem do header global (useSearch)
  const visibleRows = useMemo(() => {
    const term = (ticketSearchParam || "").trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (term) {
        const hay = `${r.name || ""} ${r.email || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, ticketSearchParam, statusFilter]);

  // === Stats ===
  const stats = useMemo(() => {
    const total = rows.length;
    const overdue = rows.filter((r) => r.status === STATUS.overdue).length;
    const trial = rows.filter((r) => r.status === STATUS.trial).length;
    const activeRevenue = rows
      .filter(
        (r) =>
          r.status === STATUS.active ||
          r.status === STATUS.pending ||
          r.status === STATUS.overdue
      )
      .reduce((sum, r) => sum + r.planAmount, 0);
    const previewCurrency =
      rows.find((r) => r.currency)?.currency || selectedCurrency?.code || "USD";
    return {
      total,
      overdue,
      trial,
      activeRevenue,
      previewCurrency,
    };
  }, [rows, selectedCurrency]);

  // === Status meta ===
  const statusMeta = (s) => {
    switch (s) {
      case STATUS.active:
        return {
          label: t("financeiro.statusList.active", { defaultValue: "Em dia" }),
          chip: classes.statusActive,
          Icon: CheckCircle2,
        };
      case STATUS.overdue:
        return {
          label: t("financeiro.statusList.overdue", {
            defaultValue: "Atrasado",
          }),
          chip: classes.statusOverdue,
          Icon: AlertTriangle,
        };
      case STATUS.trial:
        return {
          label: t("financeiro.statusList.trial", {
            defaultValue: "Trial",
          }),
          chip: classes.statusTrial,
          Icon: Sparkles,
        };
      case STATUS.pending:
        return {
          label: t("financeiro.statusList.pending", {
            defaultValue: "Pendente",
          }),
          chip: classes.statusPending,
          Icon: Clock,
        };
      case STATUS.inactive:
      default:
        return {
          label: t("financeiro.statusList.inactive", {
            defaultValue: "Inativo",
          }),
          chip: classes.statusInactive,
          Icon: XCircle,
        };
    }
  };

  const statusOptions = [
    { value: "all", label: t("financeiro.filters.all", { defaultValue: "Todos os status" }) },
    { value: STATUS.active, label: statusMeta(STATUS.active).label },
    { value: STATUS.pending, label: statusMeta(STATUS.pending).label },
    { value: STATUS.overdue, label: statusMeta(STATUS.overdue).label },
    { value: STATUS.trial, label: statusMeta(STATUS.trial).label },
    { value: STATUS.inactive, label: statusMeta(STATUS.inactive).label },
  ];
  const selectedStatusOption =
    statusOptions.find((o) => o.value === statusFilter) || statusOptions[0];

  const buildChargeMessage = (row) => {
    const valor = formatCurrency(row.planAmount, row.currency);
    const venc = row.due ? row.due.format("DD/MM/YYYY") : "—";
    const linkInvoice = row.openInvoice?.linkInvoice || "";
    const plano = row.plan?.name || "";

    const linhas = [
      `Olá ${row.name || ""},`,
      "",
      plano
        ? `Identificamos uma pendência na sua mensalidade do plano ${plano}.`
        : "Identificamos uma pendência na sua mensalidade.",
      `Valor: ${valor}`,
      `Vencimento: ${venc}`,
    ];
    if (linkInvoice) {
      linhas.push("", `Para regularizar, acesse: ${linkInvoice}`);
    }
    return linhas.join("\n");
  };

  const openCharge = (row) => {
    setChargeRow(row);
    setChargeMessage(buildChargeMessage(row));
    setChargeOpen(true);
  };

  const closeCharge = () => {
    setChargeOpen(false);
    setChargeRow(null);
    setChargeMessage("");
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(chargeMessage);
      toast.success(
        t("financeiro.charge.copied", {
          defaultValue: "Mensagem copiada!",
        })
      );
    } catch {
      toast.error(
        t("financeiro.charge.copyError", {
          defaultValue: "Não foi possível copiar.",
        })
      );
    }
  };

  const renderDueInfo = (row) => {
    if (!row.due) return <span>—</span>;
    const txt = row.due.format("DD/MM/YYYY");
    let hint = "";
    if (row.daysUntilDue === 0) {
      hint = t("financeiro.status.dueToday", { defaultValue: "Vence hoje" });
    } else if (row.daysUntilDue > 0) {
      hint = t("financeiro.status.dueIn", {
        days: row.daysUntilDue,
        defaultValue: `Vence em ${row.daysUntilDue} dias`,
      });
    } else {
      hint = t("financeiro.status.overdueFor", {
        days: Math.abs(row.daysUntilDue),
        defaultValue: `Atrasado há ${Math.abs(row.daysUntilDue)} dias`,
      });
    }
    return (
      <div>
        <div className={classes.dueDate}>{txt}</div>
        <div className={classes.dueHint}>{hint}</div>
      </div>
    );
  };

  const renderStatusChip = (s) => {
    const meta = statusMeta(s);
    const Icon = meta.Icon;
    return (
      <span className={`${classes.statusChip} ${meta.chip}`}>
        <Icon size={13} strokeWidth={2.2} />
        {meta.label}
      </span>
    );
  };

  const renderActionBtn = (row) => {
    // Tela consultiva: ações de cobrança só pro super-admin.
    if (!isSuperAdmin) return <span style={{ color: "#94a3b8" }}>—</span>;

    if (row.status === STATUS.active) {
      return (
        <span className={classes.payBtnGhost}>
          <CheckCircle2 size={14} strokeWidth={2.2} />
          {t("financeiro.buttons.paid", { defaultValue: "Pago" })}
        </span>
      );
    }
    if (row.status === STATUS.inactive) {
      return <span style={{ color: "#94a3b8" }}>—</span>;
    }
    return (
      <button
        type="button"
        className={classes.payBtn}
        onClick={() => openCharge(row)}
      >
        <Send size={14} strokeWidth={2.2} />
        {t("financeiro.buttons.chargeClient", {
          defaultValue: "Cobrar cliente",
        })}
      </button>
    );
  };

  // === Stats cards ===
  const renderStatsCards = () => (
    <div className={classes.statsGrid}>
      <StatCard
        classes={classes}
        icon={<Building2 size={20} strokeWidth={2} />}
        iconBg="rgba(99,102,241,0.12)"
        iconColor={theme.palette.primary.main}
        label={t("financeiro.stats.total", { defaultValue: "Empresas" })}
        value={stats.total}
        hint={t("financeiro.stats.totalHint", {
          defaultValue: "Cadastradas no sistema",
        })}
      />
      <StatCard
        classes={classes}
        icon={<DollarSign size={20} strokeWidth={2} />}
        iconBg="rgba(16,185,129,0.12)"
        iconColor="#10b981"
        label={t("financeiro.stats.revenue", {
          defaultValue: "Receita ativa",
        })}
        value={formatCurrency(stats.activeRevenue, stats.previewCurrency)}
        hint={t("financeiro.stats.revenueHint", {
          defaultValue: "Recorrência mensal estimada",
        })}
      />
      <StatCard
        classes={classes}
        icon={<AlertTriangle size={20} strokeWidth={2} />}
        iconBg="rgba(229,57,53,0.12)"
        iconColor="#e53935"
        label={t("financeiro.stats.overdue", {
          defaultValue: "Em atraso",
        })}
        value={stats.overdue}
        hint={t("financeiro.stats.overdueHint", {
          defaultValue: "Pagamentos pendentes",
        })}
      />
      <StatCard
        classes={classes}
        icon={<Sparkles size={20} strokeWidth={2} />}
        iconBg="rgba(99,102,241,0.12)"
        iconColor={theme.palette.primary.main}
        label={t("financeiro.stats.trial", { defaultValue: "Em trial" })}
        value={stats.trial}
        hint={t("financeiro.stats.trialHint", {
          defaultValue: "Sem pagamento ainda",
        })}
      />
    </div>
  );

  // === Table ===
  const renderTable = () => (
    <table className={classes.table}>
      <thead>
        <tr>
          <th className={classes.th}>
            {t("financeiro.table.company", { defaultValue: "Empresa" })}
          </th>
          <th className={classes.th}>
            {t("financeiro.table.plan", { defaultValue: "Plano" })}
          </th>
          <th className={classes.th}>
            {t("financeiro.table.value", { defaultValue: "Valor mensal" })}
          </th>
          <th className={classes.th}>
            {t("financeiro.table.dueDate", { defaultValue: "Vencimento" })}
          </th>
          <th className={classes.th}>
            {t("financeiro.table.status", { defaultValue: "Status" })}
          </th>
          <th className={classes.th} style={{ textAlign: "right" }}>
            {t("financeiro.table.action", { defaultValue: "Ação" })}
          </th>
        </tr>
      </thead>
      <tbody>
        {visibleRows.length === 0 ? (
          <tr>
            <td colSpan={6} className={classes.emptyState}>
              {ticketSearchParam || statusFilter !== "all"
                ? t("financeiro.empty.filtered", {
                    defaultValue:
                      "Nenhuma empresa encontrada com esses filtros.",
                  })
                : t("financeiro.empty.list", {
                    defaultValue: "Nenhuma empresa cadastrada.",
                  })}
            </td>
          </tr>
        ) : (
          visibleRows.map((row) => (
            <tr key={row.id} className={classes.tr}>
              <td className={classes.td}>
                <div className={classes.companyCell}>
                  <Avatar className={classes.companyAvatar}>
                    {getInitials(row.name)}
                  </Avatar>
                  <div style={{ minWidth: 0 }}>
                    <div className={classes.companyName} title={row.name}>
                      {row.name || "—"}
                    </div>
                    {row.displayEmail && (
                      <div className={classes.companyEmail} title={row.displayEmail}>
                        {row.displayEmail}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className={classes.td}>
                {row.plan?.name ? (
                  <span className={classes.planChip}>{row.plan.name}</span>
                ) : (
                  "—"
                )}
              </td>
              <td className={`${classes.td} ${classes.valueCell}`}>
                {formatCurrency(row.planAmount, row.currency)}
              </td>
              <td className={classes.td}>{renderDueInfo(row)}</td>
              <td className={classes.td}>{renderStatusChip(row.status)}</td>
              <td className={classes.td} style={{ textAlign: "right" }}>
                {renderActionBtn(row)}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  // === Mobile cards ===
  const renderMobileCards = () => {
    if (visibleRows.length === 0) {
      return (
        <div className={classes.emptyState}>
          {searchTerm || statusFilter !== "all"
            ? t("financeiro.empty.filtered", {
                defaultValue:
                  "Nenhuma empresa encontrada com esses filtros.",
              })
            : t("financeiro.empty.list", {
                defaultValue: "Nenhuma empresa cadastrada.",
              })}
        </div>
      );
    }
    return (
      <div className={classes.mobileGrid}>
        {visibleRows.map((row) => (
          <div key={row.id} className={classes.mobileCard}>
            <div className={classes.companyCell}>
              <Avatar className={classes.companyAvatar}>
                {getInitials(row.name)}
              </Avatar>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className={classes.companyName}>{row.name || "—"}</div>
                {row.displayEmail && (
                  <div className={classes.companyEmail}>{row.displayEmail}</div>
                )}
              </div>
              {renderStatusChip(row.status)}
            </div>
            <div className={classes.mobileCardRow}>
              <span className={classes.mobileCardLabel}>
                {t("financeiro.table.plan", { defaultValue: "Plano" })}
              </span>
              <span className={classes.planChip}>{row.plan?.name || "—"}</span>
            </div>
            <div className={classes.mobileCardRow}>
              <span className={classes.mobileCardLabel}>
                {t("financeiro.table.value", { defaultValue: "Valor mensal" })}
              </span>
              <span className={classes.valueCell}>
                {formatCurrency(row.planAmount, row.currency)}
              </span>
            </div>
            <div className={classes.mobileCardRow}>
              <span className={classes.mobileCardLabel}>
                {t("financeiro.table.dueDate", { defaultValue: "Vencimento" })}
              </span>
              <div style={{ textAlign: "right" }}>{renderDueInfo(row)}</div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {renderActionBtn(row)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <MainContainer>
      <div className={classes.container}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <Wallet size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {t("financeiro.title")}
              <span className={classes.countChip}>{stats.total}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {t("financeiro.subtitle", {
                defaultValue:
                  "Acompanhe o status das empresas, planos e cobranças.",
              })}
            </div>
          </div>
        </div>
      </div>

      {renderStatsCards()}

      <div className={classes.filtersBar}>
        <div className={classes.filterField}>
          <Autocomplete
            size="small"
            options={statusOptions}
            value={selectedStatusOption}
            onChange={(_, v) => setStatusFilter(v?.value || "all")}
            getOptionLabel={(o) => o?.label || ""}
            getOptionSelected={(o, v) => o?.value === v?.value}
            disableClearable
            classes={{ paper: dropdownClasses.paper }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={t("financeiro.filters.status", {
                  defaultValue: "Status",
                })}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Filter size={14} strokeWidth={2} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {statusFilter !== "all" && (
                        <IconButton
                          size="small"
                          className={classes.filterClearBtn}
                          onMouseDown={(e) => {
                            // evita que o Autocomplete abra antes do clear
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setStatusFilter("all");
                          }}
                          aria-label={t("financeiro.filters.clear", {
                            defaultValue: "Limpar filtro",
                          })}
                        >
                          <XIcon size={14} strokeWidth={2.4} />
                        </IconButton>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>
      </div>

      <div className={classes.listCard}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <div className={classes.tableScroll}>
            {isMobile ? renderMobileCards() : renderTable()}
          </div>
        )}
      </div>

      <Dialog
        open={chargeOpen}
        onClose={closeCharge}
        aria-labelledby="charge-dialog"
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.chargeHeader}>
          <Typography className={classes.chargeTitle}>
            <Send size={16} strokeWidth={2} />
            {t("financeiro.charge.title", {
              defaultValue: "Cobrar cliente",
            })}
          </Typography>
          <IconButton
            size="small"
            onClick={closeCharge}
            className={classes.chargeCloseBtn}
            aria-label="close"
          >
            <XIcon size={18} strokeWidth={2.2} />
          </IconButton>
        </div>
        <DialogContent dividers>
          {chargeRow && (
            <div className={classes.chargeBody}>
              <div className={classes.chargeInfoBlock}>
                <div className={classes.chargeInfoRow}>
                  <span className={classes.chargeInfoLabel}>
                    {t("financeiro.table.company", {
                      defaultValue: "Empresa",
                    })}
                  </span>
                  <span className={classes.chargeInfoValue}>
                    {chargeRow.name || "—"}
                  </span>
                </div>
                <div className={classes.chargeInfoRow}>
                  <span className={classes.chargeInfoLabel}>
                    {t("financeiro.charge.phone", {
                      defaultValue: "Telefone",
                    })}
                  </span>
                  <span className={classes.chargeInfoValue}>
                    {chargeRow.displayPhone || "—"}
                  </span>
                </div>
                <div className={classes.chargeInfoRow}>
                  <span className={classes.chargeInfoLabel}>
                    {t("financeiro.charge.email", {
                      defaultValue: "E-mail",
                    })}
                  </span>
                  <span className={classes.chargeInfoValue}>
                    {chargeRow.displayEmail || "—"}
                  </span>
                </div>
                <div className={classes.chargeInfoRow}>
                  <span className={classes.chargeInfoLabel}>
                    {t("financeiro.table.value", {
                      defaultValue: "Valor mensal",
                    })}
                  </span>
                  <span className={classes.chargeInfoValue}>
                    {formatCurrency(chargeRow.planAmount, chargeRow.currency)}
                  </span>
                </div>
                <div className={classes.chargeInfoRow}>
                  <span className={classes.chargeInfoLabel}>
                    {t("financeiro.table.dueDate", {
                      defaultValue: "Vencimento",
                    })}
                  </span>
                  <span className={classes.chargeInfoValue}>
                    {chargeRow.due
                      ? chargeRow.due.format("DD/MM/YYYY")
                      : "—"}
                  </span>
                </div>
              </div>

              <div>
                <div className={classes.chargeMessageLabel}>
                  {t("financeiro.charge.messageLabel", {
                    defaultValue: "Mensagem",
                  })}
                </div>
                <TextField
                  multiline
                  minRows={5}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.chargeMessageField}
                  value={chargeMessage}
                  onChange={(e) => setChargeMessage(e.target.value)}
                />
              </div>
            </div>
          )}
        </DialogContent>
        <ChatiaDialogActions
          type="button"
          onClick={handleCopyMessage}
          saveText={t("financeiro.charge.copy", {
            defaultValue: "Copiar mensagem",
          })}
        />
      </Dialog>
      </div>
    </MainContainer>
  );
};

const StatCard = ({ classes, icon, iconBg, iconColor, label, value, hint }) => (
  <div className={classes.statCard}>
    <span
      className={classes.statIcon}
      style={{ background: iconBg, color: iconColor }}
    >
      {icon}
    </span>
    <div className={classes.statContent}>
      <div className={classes.statLabel}>{label}</div>
      <div className={classes.statValue}>{value}</div>
      {hint && <div className={classes.statHint}>{hint}</div>}
    </div>
  </div>
);

export default Financeiro;
